
import React, { useState } from 'react';
import { LayoutDashboard, FolderCog, Menu, CheckSquare, ChevronUp, ChevronDown, Plus, GraduationCap } from 'lucide-react';
import { AppView } from '../types';
import { useNotifications } from '../hooks/useNotifications';
import { badgeText } from './notifications/notificationFormat';
import { appVariante } from '../services/nativePlatform';
import { ansichtInAppErlaubt } from '../services/appUmfang';

interface Props {
  view: AppView;
  setView: (view: AppView) => void;
  onToggleMenu: () => void;
  onFabClick: () => void; // Erfassen-Button IN der Leiste — ein schwebender FAB verdeckte früher Inhalt
  /**
   * Angemeldeter Nutzer: mit Kennung liest die Leiste den Mitteilungsstand
   * selbst aus dem gemeinsamen Speicher (useNotifications) — derselbe Stand
   * wie Glocke und Dashboard, und er faellt sofort, wenn dort quittiert wird.
   */
  userId?: string | null;
  /** Seite `#/app` offen: dann ist keine Schaltflaeche darunter „aktiv". */
  appSeiteAktiv?: boolean;
  /**
   * Darf die angemeldete Person die Ansicht sehen (Rollensystem)? App.tsx
   * reicht dieselbe Regel herein, die auch die Ansichts-Wache und die
   * Seitenleiste nutzen (`ansichtErlaubt`). Ohne: alles wie bisher.
   */
  darfAnsicht?: (view: AppView) => boolean;
}

const ALLES_ERLAUBT = () => true;

// Eine Schaltflaeche der Leiste. Die aktive traegt einen Punkt unter dem
// Symbol: Farbe allein unterscheidet fuer farbfehlsichtige Nutzer nicht
// zuverlaessig, und auf hellem Grund ist Blau gegen Grau ohnehin schwach.
const NavBtn: React.FC<{
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
  /** Zahl am Symbol (1–9, „9+"); leer = kein Abzeichen. */
  badge?: string;
}> = ({ icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    aria-current={active ? 'page' : undefined}
    className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-1 h-full rounded-xl active:bg-slate-100 transition-colors ${active ? 'text-blue-700' : 'text-slate-500'}`}
  >
    <div className="relative">
      <span className={`block transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${active ? 'scale-110' : ''}`}>{icon}</span>
      {badge && (
        <span className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] font-semibold leading-[14px] text-center tabular border-2 border-white">
          {badge}
        </span>
      )}
    </div>
    {/* truncate + min-w-0 am Knopf: sechs Eintraege auf 360 px. Ohne min-w-0
        schrumpft ein Flex-Kind nicht unter seinen Inhalt, und die Leiste
        liefe ueber statt den Text zu kuerzen. */}
    <span className="text-[11px] font-medium truncate max-w-full">{label}</span>
    <span className={`h-1 w-1 rounded-full bg-blue-600 transition-opacity duration-200 ${active ? 'opacity-100' : 'opacity-0'}`} />
  </button>
);

export const MobileNavigation: React.FC<Props> = ({ view, setView, onToggleMenu, onFabClick, userId, appSeiteAktiv = false, darfAnsicht = ALLES_ERLAUBT }) => {
  const [isOpen, setIsOpen] = useState(false);
  // Mit Kennung zaehlt der gemeinsame Speicher; ohne bleibt der Prop-Wert.
  const shared = useNotifications(userId, !!userId);
  const unread = userId ? shared.unreadCount : 0;
  const badge = badgeText(unread);

  const isActive = (target: AppView) => !appSeiteAktiv && view === target;
  // Handy-App: nur Eintraege aus APP_ANSICHTEN (docs/android-app-plan.md
  // 1.4, Vertrag E11). Tablet-App und Browser: immer wahr, Leiste unveraendert.
  // Dazu die Rechte: heute stehen hier nur freie Ansichten, ein kuenftiger
  // Eintrag mit Recht faellt so von selbst weg.
  const variante = appVariante();
  const zeige = (target: AppView) => ansichtInAppErlaubt(target, variante) && darfAnsicht(target);

  const handleNav = (target: AppView) => {
      setView(target);
      setIsOpen(false);
  };

  return (
    <>
        {/* Toggle Handle. Trefferflaeche 48 px (Handschuhe), der sichtbare
            Kreis (38 px) bleibt, wo er war. Eingeklappt waechst die Flaeche
            nach allen Seiten; offen nur nach oben — nach unten laege sie
            ueber „+" und den Knoepfen der Leiste. */}
        <div
            className={`md:hidden fixed left-0 right-0 z-[91] flex justify-center transition-all duration-300 ${isOpen ? 'bottom-20' : 'bottom-[19px]'}`}
            style={{ pointerEvents: 'none' }}
        >
            <button 
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? 'Navigation schließen' : 'Navigation öffnen'}
                aria-expanded={isOpen}
                className={`w-12 h-12 flex justify-center rounded-full pointer-events-auto active:scale-95 transition-transform ${isOpen ? 'items-end' : 'items-center'}`}
            >
                <span className="relative bg-slate-900/90 text-white p-2 rounded-full shadow-lg backdrop-blur-md border border-white/10">
                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                    {/* Ungelesenes muss auch bei eingeklappter Leiste auffallen —
                        der Punkt sitzt am Griff, nicht an der versteckten Leiste. */}
                    {!isOpen && unread > 0 && (
                        <span aria-hidden="true" className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-600 rounded-full border-2 border-white" />
                    )}
                </span>
            </button>
        </div>

        {/* Navigation Bar */}
        <div
            className={`md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-slate-200 z-[90] pb-safe transition-transform duration-300 ease-in-out ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}
        >
          <div className="flex justify-around items-center h-20 px-1 pb-2 gap-0.5">
            
            {zeige('dashboard') && <NavBtn icon={<LayoutDashboard className="w-6 h-6" />} label="Home" active={isActive('dashboard')} onClick={() => handleNav('dashboard')} />}
            {zeige('projects') && <NavBtn icon={<FolderCog className="w-6 h-6" />} label="Projekte" active={isActive('projects')} onClick={() => handleNav('projects')} />}

            {/* Erfassen: sitzt in der Leiste, verschwindet mit ihr — verdeckt nie Inhalt */}
            <button
              onClick={() => { onFabClick(); setIsOpen(false); }}
              aria-label="Neu erfassen"
              className="w-14 h-14 shrink-0 bg-gradient-to-b from-blue-500 to-blue-600 text-white rounded-full shadow-lg shadow-blue-600/25 ring-1 ring-inset ring-white/25 flex flex-col items-center justify-center active:scale-95 transition-transform duration-150 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
            >
              <Plus className="w-7 h-7" />
            </button>

            {zeige('tasks') && <NavBtn icon={<CheckSquare className="w-6 h-6" />} label="Aufgaben" active={isActive('tasks')} onClick={() => handleNav('tasks')} badge={badge} />}
            {/* Lernzentrum auch unterwegs: wer sich draussen nicht sicher ist,
                hat das Handy in der Hand und nicht den Rechner. Das Symbol
                allein traegt die Zeile — der Text darunter ist bewusst kurz,
                sechs Eintraege muessen bei 360 px nebeneinander passen. */}
            {zeige('lernzentrum') && <NavBtn icon={<GraduationCap className="w-6 h-6" />} label="Lernen" active={isActive('lernzentrum')} onClick={() => handleNav('lernzentrum')} />}
            <NavBtn icon={<Menu className="w-6 h-6" />} label="Menü" onClick={() => { onToggleMenu(); setIsOpen(false); }} />

          </div>
          {/* Safe Area Spacer for iOS Home Indicator */}
          <div className="h-[env(safe-area-inset-bottom)] bg-white/95 backdrop-blur-xl"></div>
        </div>
    </>
  );
};
